import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close'; // Inserito per la funzione di clear veloce
import HomeIcon from '@mui/icons-material/Home';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Breadcrumbs,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useIsDirector } from 'shared/permissions/useIsDirector';
import { useIsTechnician } from 'shared/permissions/useIsTechnician';
import { useTranslation } from 'react-i18next';
import { IFolder } from '../types';
import { AddDocumentDialog } from './AddDocumentDialog';
import UploadDocuments from './UploadDocuments';

interface DocumentsToolbarProps {
  /** La cartella attualmente aperta nell'esploratore */
  currentFolder: IFolder | null;
  /** La cartella radice (root) del sistema documentale */
  rootFolder: IFolder | null;
  allFolders: IFolder[];
  /** Valore testuale del filtro di ricerca corrente */
  filter: string;
  /** Callback per impostare la cartella corrente (es: quando si clicca Home) */
  setCurrentFolder: (folder: IFolder | null) => void;
  /** Funzione per gestire il ritorno alla cartella padre */
  handleGoBack: () => void;
  /** Funzione per aggiornare il valore del filtro di ricerca */
  setFilter: (value: string) => void;
  /** Callback per resettare la lista della ricerca avanzata */
  setAdvancedSearchList: (list: any[]) => void;
  /** Funzione che fa scattare la ricerca avanzata lato server */
  advanceSearch: () => void;
  /** Callback attivata al completamento del caricamento di un nuovo file */
  onSubmitNewDocument: (values: any) => void;
  /** Componente custom per l'upload massivo di file/cartelle */
  setIsSearching: (value: boolean) => void;
}

/**
 * DocumentsToolbar Component
 *
 * Un pannello di comando isolato e reattivo per la gestione e la navigazione del file explorer.
 * Centralizza i controlli di ricerca avanzata, i pulsanti di tracciamento ad albero e i trigger di upload.
 */
export const DocumentsToolbar = ({
  currentFolder,
  rootFolder,
  allFolders,
  filter,
  setCurrentFolder,
  handleGoBack,
  setFilter,
  setAdvancedSearchList,
  advanceSearch,
  onSubmitNewDocument,
  setIsSearching
}: DocumentsToolbarProps) => {
  const { t } = useTranslation();
  const translationPrefix = 'pages.document';
  const isDisableEdit = useIsTechnician() || useIsDirector();

  /**
   * Genera l'array ordinato delle cartelle antenate per costruire le briciole di pane interattive.
   * Parte dalla cartella corrente e risale i 'parent' fino alla radice.
   */
  const buildBreadcrumbsPath = (): IFolder[] => {
    if (!currentFolder || !allFolders.length) return [];

    if (currentFolder.id === rootFolder?.id) return [];

    const path: IFolder[] = [];
    let current: IFolder | undefined = currentFolder;

    while (current && current.id !== rootFolder?.id) {
      path.unshift(current);
      current = allFolders.find((f) => f.id === current?.parent);
    }
    return path;
  };
  const breadcrumbsPath = buildBreadcrumbsPath();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        mb: 4,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        gap: 3,
        position: 'relative',
        flexWrap: { xs: 'wrap', md: 'nowrap' }
      }}>
      {/* CONTROLLI DI NAVIGAZIONE (INDIETRO / HOME) */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 0.5,
          flexGrow: 1,
          minWidth: 0,
          mr: { md: 2 }
        }}>
        <Tooltip title={t('actions.back')}>
          <span>
            <IconButton
              onClick={handleGoBack}
              disabled={!currentFolder || currentFolder.parent === null}
              sx={{ width: 40, height: 40 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title={t('global.documents')}>
          <IconButton
            onClick={() => {
              setCurrentFolder(rootFolder);
              setFilter('');
              setAdvancedSearchList([]);
              setIsSearching(false);
            }}
            sx={{
              width: 40,
              height: 40,
              color:
                !currentFolder || currentFolder.id === rootFolder?.id
                  ? 'primary.main'
                  : 'action.active'
            }}>
            <HomeIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Typography sx={{ color: 'text.disabled', mx: 0.5, userSelect: 'none' }}>/</Typography>

        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" sx={{ color: 'text.disabled' }} />}
          aria-label="breadcrumb"
          sx={{
            minWidth: 0,
            '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', overflow: 'hidden' }
          }}>
          {breadcrumbsPath.map((folder, index) => {
            const isLast = index === breadcrumbsPath.length - 1;

            return isLast ? (
              <Typography
                key={`crumb-${folder.id}`}
                variant="body2"
                sx={{
                  color: 'text.primary',
                  maxWidth: 140,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                {t(folder.name.split('/').pop() || folder.name)}
              </Typography>
            ) : (
              <Button
                key={`crumb-${folder.id}`}
                variant="text"
                size="small"
                onClick={() => setCurrentFolder(folder)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: 0,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  maxWidth: 120,
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'transparent',
                    textDecoration: 'underline'
                  }
                }}>
                {t(folder.name.split('/').pop() || folder.name)}
              </Button>
            );
          })}
        </Breadcrumbs>
      </Box>

      {/* BOTTONI OPERATIVI E RICERCA */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexGrow: 1,
          justifyContent: 'end',
          order: { xs: 3, md: 3 },
          width: { xs: '100%', md: 'auto' },
          maxWidth: { xs: '100%', md: '55%', lg: '50%' }
        }}>
        <TextField
          variant="outlined"
          size="small"
          placeholder={'Cerca ...'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  size="small"
                  onClick={advanceSearch}
                  edge="start"
                  sx={{ p: 0.5, '&:hover': { color: 'primary.main' } }}>
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </IconButton>
              </InputAdornment>
            ),
            endAdornment: filter ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setFilter('');
                    setAdvancedSearchList([]);
                    setIsSearching(false);
                  }}
                  edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
          sx={{
            width: '100%',
            maxWidth: 240,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              height: 40
            }
          }}
          value={filter}
          onChange={(e) => {
            const value = e.target.value;
            setAdvancedSearchList([]);
            setFilter(value);
            setIsSearching(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') advanceSearch();
          }}
        />

        {!isDisableEdit && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              flexShrink: 0,
              '& button, & .MuiButton-root': {
                height: 40,
                borderRadius: 2
              },
              '& > *:last-child button, & > *:last-child .MuiButton-root': {
                minWidth: 40,
                width: 40,
                p: 0
              }
            }}>
            <UploadDocuments isDisabled={currentFolder && currentFolder.name !== '/'} />

            <AddDocumentDialog
              onSubmit={onSubmitNewDocument}
              currentFolder={currentFolder ? currentFolder.name : '/'}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};
